import {useMutation, useQuery} from '@apollo/client'
import {FormEvent, useEffect, useState} from 'react'
import {Row, Col, Form, Spinner} from 'react-bootstrap'
import Select from 'react-select'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import {FormattedMessage, useIntl} from 'react-intl'
import {CREATE_COMMISSION_REPORT} from '../../../gql/mutations/commissionReportMutations'
import {toast} from 'react-toastify'
import {IResCreateCommissionReport, NodeCommissionReport} from '../../../types/commissionReportApi'
import {GET_STATES_EU} from '../../../gql/queries/statesEUQuery'
import {IResStates} from '../../../types/statesEU'
import {GET_ALL_CARRIERS_LIST} from '../../../gql/queries/carriersQuery'
import {IResCarriersList} from '../../../types/carriers'
import {parseId} from '../../../helpers'

interface IModalProps {
  keyboard: boolean
  backdrop: string
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  idFormEdit?: NodeCommissionReport | null
}

const MONTH_IDS = [
  'common.month.january','common.month.february','common.month.march','common.month.april','common.month.may','common.month.june',
  'common.month.july','common.month.august','common.month.september','common.month.october','common.month.november','common.month.december',
]
const MONTH_DEFAULTS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
]

const getYearOptions = () => {
  const currentYear = new Date().getFullYear()
  return Array.from({length: 6}, (_, i) => currentYear - 4 + i)
}

export const ModalAddCommissionReport: React.FC<IModalProps> = ({isOpen, onClose, refetch, idFormEdit}) => {
  const intl = useIntl()
  const months = MONTH_IDS.map((id, i) => intl.formatMessage({id, defaultMessage: MONTH_DEFAULTS[i]}))
  const selectOne = intl.formatMessage({id: 'commissions.reports.selectOne', defaultMessage: 'Select one'})
  const [gqlCreate, {data, loading, error}] = useMutation<IResCreateCommissionReport>(CREATE_COMMISSION_REPORT)
  const {data: dataCarriers} = useQuery<IResCarriersList>(GET_ALL_CARRIERS_LIST, {
    fetchPolicy: 'no-cache',
  })
  const {data: dataStates} = useQuery<IResStates>(GET_STATES_EU)
  
  const [formData, setFormData] = useState({
    name: '',
    effectiveMonth: '',
    effectiveYear: '',
    includePreviousMonth: false,
    approvePreviousYear: false,
    carriers: [] as {value: string; label: string}[],
    states: [] as {value: string; label: string}[],
  })


  useEffect(() => {
    if (!data) return

    const response = data.commissionReportCreateMutation
    if (response?.commissionReport?.id) {
      toast.success(intl.formatMessage({id: 'commissions.reports.add.toast.success', defaultMessage: 'Commission Report added successfully'}))
      refetch()
      onClose()
    } else if (response?.errors?.length) {
      toast.error(intl.formatMessage(
        {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
        {message: response.errors[0]?.message || intl.formatMessage({id: 'commissions.reports.somethingWrong', defaultMessage: 'Something went wrong!'})}
      ))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    if (error) toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: error.message}))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])


  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({...prev, [field]: value}))
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!formData.name || !formData.effectiveMonth || !formData.effectiveYear) return

    gqlCreate({
      variables: {
        name: formData.name,
        effectiveMonth: Number(formData.effectiveMonth),
        effectiveYear: Number(formData.effectiveYear),
        carriers: formData.carriers.map(c => parseId(c.value)).filter(id => id !== null),
        statesEU: formData.states.map(state => parseId(state.value)).filter(id => id !== null),
        approvePreviousYear: formData.approvePreviousYear,
      },
    })
  }

  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>
          <FormattedMessage id='commissions.reports.add.title' defaultMessage='Create a new commissions report' />
        </Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <div>
            <Row className='mb-3'>
              <Col md={12}>
                <Form.Label>
                  <FormattedMessage id='commissions.reports.field.reportTitle' defaultMessage='Report title (required)' />
                </Form.Label>
                <Form.Control
                  type='text'
                  placeholder={intl.formatMessage({id: 'commissions.reports.field.reportTitlePlaceholder', defaultMessage: 'Enter report title'})}
                  name='name'
                  required
                  value={formData.name}
                  onChange={e => handleChange('name', e.target.value)}
                />
              </Col>
            </Row>
            <Row className='mb-3'>
              <Col md={6}>
                <Form.Label>
                  <FormattedMessage id='commissions.reports.field.effectiveMonth' defaultMessage='Effective month (required)' />
                </Form.Label>
                <Form.Select
                  required
                  value={formData.effectiveMonth}
                  onChange={e => handleChange('effectiveMonth', e.target.value)}
                >
                  <option value=''>{selectOne}</option>
                  {months.map((month, index) => (
                    <option key={index} value={index + 1}>{month}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>
                  <FormattedMessage id='commissions.reports.field.effectiveYear' defaultMessage='Effective year (required)' />
                </Form.Label>
                <Form.Select
                  required
                  value={formData.effectiveYear}
                  onChange={e => handleChange('effectiveYear', e.target.value)}
                >
                  <option value=''>{selectOne}</option>
                  {getYearOptions().map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
            <Row className='mb-3'>
              <Col md={12}>
                <Form.Label>
                  <FormattedMessage id='commissions.reports.field.companies' defaultMessage='Companies to be processed (optional)' />
                </Form.Label>
                <Select
                  options={dataCarriers?.allInsuranceOperatorsList.map(carrier => ({
                    value: String(parseId(carrier.id)),
                    label: carrier.name,
                  })) || []}
                  value={formData.carriers}
                  onChange={selected => handleChange('carriers', selected || [])}
                  isMulti
                  isClearable
                />
                <div className='form-text'>
                  <FormattedMessage id='commissions.reports.companiesHint' defaultMessage='You can select multiple carriers (e.g. United One, United One - Vision, United One - Dental). If none selected, the report will be empty.' />
                </div>
              </Col>
            </Row>
            <Row className='mb-3'>
              <Col md={12}>
                <Form.Label>
                  <FormattedMessage id='commissions.reports.field.states' defaultMessage='States to be processed (optional)' />
                </Form.Label>
                <Select
                  options={dataStates?.allStates?.edges.map(({node}) => ({
                    value: node.id,
                    label: `${node.name} (${node.code})`,
                  })) || []}
                  value={formData.states}
                  onChange={selected => handleChange('states', selected || [])}
                  isMulti
                  isClearable
                />
                <div className='form-text'>
                  <FormattedMessage id='commissions.reports.statesHint' defaultMessage='If you do not select any state, policies from all the states will be processed.' />
                </div>
              </Col>
            </Row>
            <Row className='mb-3'>
              <Col md={12}>
                <Form.Check
                  type='switch'
                  id='approvePreviousYearSwitch'
                  label={intl.formatMessage({id: 'commissions.reports.approvePrevYear', defaultMessage: 'Approve policies found in previous year'})}
                  checked={formData.approvePreviousYear}
                  onChange={e => handleChange('approvePreviousYear', e.target.checked)}
                />
                <div className='form-text'>
                  <FormattedMessage id='commissions.reports.approvePrevYearHint' defaultMessage='If enabled, policies found in the previous year will be marked as APPROVED_DISCREPANCY instead of NOT_FOUND.' />
                </div>
              </Col>
            </Row>

          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button type='button' variant='secondary' onClick={onClose}>
            <FormattedMessage id='common.action.close' defaultMessage='Close' />
          </Button>
          <Button type='submit' variant='primary' disabled={loading}>
            {loading && <Spinner animation='border' size='sm' className='me-2' />}
            <FormattedMessage id='commissions.logs.submit' defaultMessage='Submit' />
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}
